import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useLang } from "../i18n";
import { useAuth } from "../AuthContext";
import { api } from "../api";
import { PROTOCOL_FOODS } from "../constants/protocolFoods";
import { MarkdownLite } from "../components/MarkdownLite";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { AiDisclaimer } from "../components/AiDisclaimer";
import html2canvas from "html2canvas";
import { ArrowLeft, Sparkles, Stethoscope, ArrowUpRight, Download, Share2 } from "lucide-react";
import { RiseLogo } from "../components/RiseLogo";

export default function Reports() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [longevity, setLongevity] = useState(null);
  const [shareReport, setShareReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("practical");
  const pollRef = useRef(null);
  const shareCardRef = useRef(null);

  const load = () => api.get("/reports").then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/longevity").then((r) => setLongevity(r.data)).catch(() => {});
    return () => clearTimeout(pollRef.current);
  }, []);

  useEffect(() => {
    if (!shareReport) return;
    let cancelled = false;
    (async () => {
      await new Promise((r) => setTimeout(r, 150));
      if (cancelled || !shareCardRef.current) return;
      try {
        const canvas = await html2canvas(shareCardRef.current, { scale: 2, backgroundColor: "#2B4A59" });
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        const file = new File([blob], "rise-report.png", { type: "image/png" });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: "RISE your LIFE" });
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url; a.download = "rise-report.png"; a.click();
          URL.revokeObjectURL(url);
          toast.success(t("share_ready"));
        }
      } catch (e) {
        if (e && e.name !== "AbortError") toast.error(lang === "it" ? "Errore" : "Error");
      } finally {
        setShareReport(null);
      }
    })();
    return () => { cancelled = true; };
  }, [shareReport]);

  const downloadPdf = (r) => {
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 48;
    const maxW = pageW - margin * 2;
    let y = 56;

    pdf.setFont("helvetica", "bold"); pdf.setFontSize(22); pdf.setTextColor(42, 59, 87);
    pdf.text("RISE your LIFE", margin, y); y += 22;
    pdf.setFont("helvetica", "normal"); pdf.setFontSize(11); pdf.setTextColor(110, 107, 101);
    pdf.text(`${lang === "it" ? "Report settimanale" : "Weekly report"} · ${new Date(r.generated_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US")} · ${r.days_count} ${lang === "it" ? "giorni" : "days"}`, margin, y);
    y += 28;

    const ensure = (h) => { if (y + h > pageH - margin) { pdf.addPage(); y = margin; } };
    const write = (text, { size = 11, bold = false, color = [40, 40, 40], gap = 6, indent = 0 } = {}) => {
      pdf.setFont("helvetica", bold ? "bold" : "normal");
      pdf.setFontSize(size);
      pdf.setTextColor(color[0], color[1], color[2]);
      pdf.splitTextToSize(text, maxW - indent).forEach((ln) => {
        ensure(size + gap); pdf.text(ln, margin + indent, y); y += size + gap;
      });
    };

    (r.content || "").split(/\r?\n/).forEach((raw) => {
      const line = raw.trim();
      if (!line) { y += 6; return; }
      if (line.startsWith("### ")) { y += 4; write(line.slice(4).replace(/\*\*/g, ""), { size: 12, bold: true, color: [42, 59, 87] }); }
      else if (line.startsWith("## ")) { y += 6; write(line.slice(3).replace(/\*\*/g, ""), { size: 15, bold: true, color: [42, 59, 87] }); }
      else if (line.startsWith("# ")) { y += 6; write(line.slice(2).replace(/\*\*/g, ""), { size: 17, bold: true, color: [42, 59, 87] }); }
      else if (/^[-*]\s+/.test(line)) { write("•  " + line.replace(/^[-*]\s+/, "").replace(/\*\*/g, ""), { indent: 10 }); }
      else { write(line.replace(/\*\*/g, "").replace(/^#+\s*/, ""), {}); }
    });

    if (r.needs_specialist) {
      y += 12; ensure(30);
      write((lang === "it" ? "Consulenza specialistica consigliata: " : "Specialist consultation suggested: ") + (r.specialist_url || "https://www.mdsmed.it"), { color: [200, 29, 107], bold: true });
    }

    if (user?.anti_inflammatory_enabled) {
      const prefer = PROTOCOL_FOODS.prefer[lang] || PROTOCOL_FOODS.prefer.it;
      const avoid = PROTOCOL_FOODS.avoid[lang] || PROTOCOL_FOODS.avoid.it;
      y += 20; ensure(40);
      write(lang === "it" ? "Protocollo Anti-infiammatorio" : "Anti-inflammatory Protocol", { size: 15, bold: true, color: [42, 59, 87] });
      y += 4;
      write(lang === "it" ? "Cibi consigliati" : "Foods to prefer", { size: 12, bold: true, color: [59, 96, 78] });
      prefer.forEach((f) => write("+  " + f, { indent: 10, color: [47, 74, 61] }));
      y += 8;
      write(lang === "it" ? "Cibi da evitare" : "Foods to avoid", { size: 12, bold: true, color: [183, 65, 14] });
      avoid.forEach((f) => write("-  " + f, { indent: 10, color: [138, 59, 22] }));
    }

    y += 20; ensure(30);
    write(t("ai_disclaimer"), { size: 9, color: [110, 107, 101] });

    pdf.save(`RISE-report-${(r.generated_at || "").slice(0, 10)}.pdf`);
  };

  const generate = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/report/generate?lang=${lang}&mode=${mode}`);
      const id = res.data.id;
      toast.info(lang === "it" ? "Report in generazione..." : "Generating report...");
      await load();
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await api.get("/reports");
          setReports(r.data);
          const target = r.data.find((x) => x.id === id);
          if (target && target.status === "done") {
            setBusy(false);
            toast.success(lang === "it" ? "Report pronto!" : "Report ready!");
            return;
          }
          if (target && target.status === "error") {
            setBusy(false);
            toast.error(lang === "it" ? "Errore nella generazione" : "Generation error");
            return;
          }
        } catch (e) {
          console.debug("Report polling retry:", e?.message || e);
        }
        if (tries > 45) { setBusy(false); return; }
        pollRef.current = setTimeout(poll, 4000);
      };
      pollRef.current = setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
      setBusy(false);
    }
  };

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-[#6E6B65] mb-4 hover:text-[#3F6E85]" data-testid="reports-back">
          <ArrowLeft className="w-4 h-4" /> {t("back")}
        </button>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-serif text-4xl tracking-tight">{t("reports")}</h1>
            <p className="text-sm text-[#6E6B65] mt-1">{t("report_mode_hint")}</p>
          </div>
          <div className="flex flex-col gap-3 sm:items-end">
            <div className="inline-flex rounded-full bg-[#F3F1ED] p-1" data-testid="report-mode-toggle">
              <button onClick={() => setMode("practical")} data-testid="report-mode-practical"
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "practical" ? "bg-[#3F6E85] text-white" : "text-[#6E6B65]"}`}>
                {t("report_practical")}
              </button>
              <button onClick={() => setMode("scientific")} data-testid="report-mode-scientific"
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "scientific" ? "bg-[#3F6E85] text-white" : "text-[#6E6B65]"}`}>
                {t("report_scientific")}
              </button>
            </div>
            <Button data-testid="reports-generate-btn" onClick={generate} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white gap-2 w-fit">
              <Sparkles className="w-4 h-4" /> {busy ? t("generating") : t("generate_report")}
            </Button>
          </div>
        </div>

        {reports.length === 0 ? (
          <div className="rounded-2xl p-10 bg-[#F3F1ED] text-center text-[#6E6B65]" data-testid="reports-empty">
            {t("no_reports")}
          </div>
        ) : (
          <div className="space-y-6" data-testid="reports-list">
            {reports.map((r) => (
              <div key={r.id} className="rounded-2xl p-8 bg-white border border-stone-200" data-testid="report-item">
                <div className="flex items-center justify-between gap-3 mb-4">
                  <p className="text-xs uppercase tracking-widest text-[#B5651D] font-semibold">
                    {new Date(r.generated_at).toLocaleString(lang === "it" ? "it-IT" : "en-US")} · {r.days_count} {lang === "it" ? "giorni" : "days"}
                  </p>
                  {r.status === "done" && (
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => setShareReport({ ...r })} className="rounded-full gap-2" data-testid="report-share-btn">
                        <Share2 className="w-4 h-4" /> {t("share_report")}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => downloadPdf(r)} className="rounded-full gap-2" data-testid="report-download-pdf-btn">
                        <Download className="w-4 h-4" /> PDF
                      </Button>
                    </div>
                  )}
                </div>
                {r.status === "pending" ? (
                  <div className="flex items-center gap-3 text-[#6E6B65]" data-testid="report-pending">
                    <span className="w-5 h-5 rounded-full border-2 border-[#3F6E85] border-t-transparent animate-spin" />
                    {lang === "it" ? "Il tuo report personalizzato è in preparazione..." : "Your personalized report is being prepared..."}
                  </div>
                ) : r.status === "error" ? (
                  <p className="text-[#B7410E]" data-testid="report-error">
                    {lang === "it" ? "Si è verificato un errore. Riprova a generare il report." : "An error occurred. Please try generating the report again."}
                  </p>
                ) : (
                  <>
                    <MarkdownLite text={r.content} />
                    {r.needs_specialist && (
                      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-2xl p-4 bg-[#C81D6B]/10 border border-[#C81D6B]/25" data-testid="report-medical-bridge">
                        <Stethoscope className="w-5 h-5 text-[#C81D6B] shrink-0" />
                        <p className="text-sm text-[#3a3a3a] flex-1">{t("specialist_hint")}</p>
                        <a
                          href={r.specialist_url || "https://www.mdsmed.it"}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="report-book-specialist-btn"
                          className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold bg-[#C81D6B] hover:bg-[#b0175d] text-white transition-colors shrink-0"
                        >
                          {t("book_specialist")} <ArrowUpRight className="w-4 h-4" />
                        </a>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}
        {reports.length > 0 && <AiDisclaimer />}

        {/* Off-screen branded share card for a report */}
        {shareReport && (
          <div style={{ position: "fixed", left: "-99999px", top: 0 }} aria-hidden="true">
            <div ref={shareCardRef} data-testid="report-share-card" style={{ width: 620 }} className="relative overflow-hidden bg-[#2B4A59] text-[#FDFBF7] p-10">
              <div className="grain opacity-40" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-8">
                  <RiseLogo width={44} height={29} />
                  <span className="font-serif text-2xl font-semibold tracking-tight">RISE your LIFE</span>
                </div>
                <p className="text-xs uppercase tracking-widest text-[#B5651D] font-semibold mb-1">{t("report_share_kicker")}</p>
                <p className="font-serif text-3xl mb-6">
                  {new Date(shareReport.generated_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-US")}
                </p>
                <div className="flex items-end gap-8 mb-8">
                  {longevity && (
                    <div>
                      <p className="font-serif text-6xl leading-none text-[#B5651D]">{longevity.score}</p>
                      <p className="text-sm text-white/60 mt-1">{t("longevity_score")}</p>
                    </div>
                  )}
                  {longevity?.bio?.delta > 0 && (
                    <div>
                      <p className="font-serif text-5xl leading-none text-[#8FD9A8]">−{longevity.bio.delta}</p>
                      <p className="text-sm text-white/60 mt-1">{t("bio_younger")}</p>
                    </div>
                  )}
                </div>
                <p className="text-white/70 text-lg mb-8">{shareReport.days_count} {t("report_share_line")}</p>
                <p className="text-sm text-white/50">{t("tagline")}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
